import { toClientViewModelList, toCreateClientRequest } from '../../../core/client/client.core.js';

export class ClientListEventAdapter {
  #apiAdapter;
  #clientListComponent;
  #notificationComponent;
  #reloadPage;

  /**
   * @param {() => void} [reloadPage] reloads the app so index.js shows the login screen
   */
  constructor(apiAdapter, clientListComponent, notificationComponent, reloadPage = location.reload.bind(location)) {
    this.#apiAdapter = apiAdapter;
    this.#clientListComponent = clientListComponent;
    this.#notificationComponent = notificationComponent;
    this.#reloadPage = reloadPage;
  }

  connect() {
    document.addEventListener('client-create', this.#onClientCreate);
    document.addEventListener('client-delete', this.#onClientDelete);
    document.addEventListener('client-select', this.#onClientSelect);
  }

  disconnect() {
    document.removeEventListener('client-create', this.#onClientCreate);
    document.removeEventListener('client-delete', this.#onClientDelete);
    document.removeEventListener('client-select', this.#onClientSelect);
  }

  async loadClients() {
    this.#clientListComponent.loading = true;
    try {
      const apiClients = await this.#apiAdapter.getAllClients();
      this.#clientListComponent.clients = toClientViewModelList(apiClients);
      this.#notificationComponent.clearError();
    } catch (error) {
      this.#handleError(error, 'Could not load clients');
    } finally {
      this.#clientListComponent.loading = false;
    }
  }

  /** Clears the create form only once the client exists, so a rejected request keeps what was typed. */
  #onClientCreate = async (event) => {
    const request = toCreateClientRequest(event.detail);
    this.#clientListComponent.loading = true;
    try {
      await this.#apiAdapter.createClient(request);
      this.#clientListComponent.resetCreateForm();
      this.#notificationComponent.show('Client created', 'success');
      await this.loadClients();
    } catch (error) {
      this.#handleError(error, 'Could not create client');
    } finally {
      this.#clientListComponent.loading = false;
    }
  };

  #onClientDelete = async (event) => {
    this.#clientListComponent.loading = true;
    try {
      await this.#apiAdapter.deleteClient(event.detail.slug);
      // The client's billing items leave the nav badge count with it.
      document.dispatchEvent(new CustomEvent('billing-changed'));
      this.#notificationComponent.show('Client deleted', 'success');
      await this.loadClients();
    } catch (error) {
      this.#handleError(error, 'Could not delete client');
    } finally {
      this.#clientListComponent.loading = false;
    }
  };

  /**
   * @param {{status?: number, message: string}} error
   * @param {string} action what failed, shown before the server's message
   */
  #handleError(error, action) {
    if (error.status === 401) {
      this.#notificationComponent.show('Session expired. Please login again.', 'error');
      setTimeout(() => this.#reloadPage(), 2000);
    } else {
      this.#notificationComponent.show(`${action}: ${error.message}`, 'error');
    }
  }

  #onClientSelect = (event) => {
    location.hash = `#/clients/${event.detail.slug}`;
  };
}
